import React from 'react';
import { Box } from '@chakra-ui/react';

import { getFormatDateFromStoreDate } from 'shared-components';
import { CourseLevelOfferStatus, ICourse, IImportCourse, ISummaryCourse } from 'api-paths-cas';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { SummaryGroup } from '../../../Summary/SummaryGroup';

interface Props {
  useImportCourses?: boolean;
  showConditionStatusInCourseDetails?: boolean;
  showCourseConditionsInCourseDetails?: boolean;
  studentNo?: string;
  courses?: Array<ICourse>;
  importCourses?: Array<IImportCourse>;
  summaryCourses?: ISummaryCourse[];
  riskList?: string[];
  showOfferEvidence?: boolean;
}

function CourseDetailsAndDeposit({
  studentNo,
  useImportCourses,
  showConditionStatusInCourseDetails,
  showCourseConditionsInCourseDetails,
  courses: allCourses,
  importCourses,
  showOfferEvidence,
  summaryCourses,
}: Props) {
  const filteredCourses = allCourses?.filter(
    course => course && course.courseName !== undefined && course.courseName !== '',
  );
  const filteredImportCourses = importCourses?.filter(
    course => course && course.title !== undefined && course.title !== '',
  );
  const courses: Array<ICourse | IImportCourse> | undefined = useImportCourses
    ? filteredImportCourses
    : filteredCourses;

  return (
    <>
      {<SummaryRow className={'summary'} value={studentNo} label={`Student Number`} />}
      {courses !== undefined && <SummaryRow className={'summary'} value={courses?.length} label={`How many courses`} />}
      {courses !== undefined && summaryCourses && (
        <Box as="ol" pl={8}>
          <SummaryGroup title="Courses">
            {courses?.map((course: ICourse | IImportCourse, index: number) => {
              const importCourse = useImportCourses
                ? (course as IImportCourse)
                : importCourses?.find(importCourse => importCourse.title === course.courseName);
              const conditions = [
                importCourse?.conditionText1,
                importCourse?.conditionText2,
                importCourse?.conditionText3,
                importCourse?.conditionText4,
                importCourse?.conditionText5,
                importCourse?.conditionText6,
                importCourse?.conditionText7,
                importCourse?.conditionText8,
                importCourse?.conditionText9,
              ]
                .filter(Boolean)
                .join('\n')
                .trim();

              const getCourseOfferStatus = (c?: IImportCourse) => {
                if (c?.isDeclined) {
                  return CourseLevelOfferStatus.DECLINED;
                }
                if (c?.conditional) {
                  return CourseLevelOfferStatus.CONDITIONAL;
                }
                return CourseLevelOfferStatus.UNCONDITIONAL;
              };

              const offerStatus = getCourseOfferStatus(importCourse);

              const summaryCourse = summaryCourses[index];
              const fee = (course as ICourse).courseFee ?? (course as IImportCourse).courseFeeFirstYear;

              return (
                <React.Fragment key={`course-${index}`}>
                  <SummaryRow label="Course Name" value={summaryCourse.title} />

                  <SummaryRow label="Start Date" value={getFormatDateFromStoreDate(summaryCourse.startDate)} />

                  <SummaryRow label="End Date" value={getFormatDateFromStoreDate(summaryCourse.endDate)} />

                  <SummaryRow label="Fee" value={fee} />

                  <SummaryRow label="Is Work Placement" value={summaryCourse.isWorkPlacement ? 'Yes' : 'No'} />

                  {course.lastAcceptanceDate && (
                    <SummaryRow
                      label="Last Acceptance Date"
                      value={getFormatDateFromStoreDate(course.lastAcceptanceDate)}
                    />
                  )}

                  {showOfferEvidence && course.offerEvidence && (
                    <SummaryRow
                      label="Evidence Used for Offer"
                      value={<Box whiteSpace={'pre-line'}>{course.offerEvidence}</Box>}
                    />
                  )}

                  {showConditionStatusInCourseDetails && <SummaryRow label="Offer Status" value={offerStatus} />}

                  {showCourseConditionsInCourseDetails && Boolean(conditions) && (
                    <SummaryRow label="Offer Status" value={<Box whiteSpace={'pre-line'}>{conditions}</Box>} />
                  )}
                </React.Fragment>
              );
            })}
          </SummaryGroup>
        </Box>
      )}
    </>
  );
}

export default CourseDetailsAndDeposit;
